/**
 * Autentificare.
 *
 * Detalii de UX care contează comercial:
 *  • destinația cerută (`?next=/admin`) este respectată după login;
 *  • administratorii de platformă ajung implicit în consola `/admin`, nu în dashboard-ul unui restaurant;
 *  • conturile demo pot fi completate cu un click, ca să nu se piardă timp într-o prezentare;
 *  • mesajul de eroare nu dezvăluie dacă emailul există sau nu (anti user enumeration).
 */
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../components/AppShell.js';
import { Button, Field, Icon, InlineAlert, Input } from '../components/ui.js';
import { ApiError } from '../lib/api.js';
import { errorMessage, useSession } from '../lib/session.js';

const DEMO_ACCOUNTS = [
  { label: 'Restaurant demo', hint: 'Green Garden — owner', email: 'demo@menuvia.ro', password: 'DemoMenuvia2026!' },
  { label: 'Admin platformă', hint: 'Consola Menuvia', email: 'admin@menuvia.ro', password: 'AdminMenuvia2026!' },
];

export function LoginPage() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Cadrul curent (previzualizare încorporată, mod privat strict) poate bloca păstrarea sesiunii.
  // În acest caz nu ascundem problema: îi spunem utilizatorului ce se întâmplă și cum o ocolește.
  const [needsOwnWindow, setNeedsOwnWindow] = useState(false);
  const [loading, setLoading] = useState(false);

  const from = searchParams.get('next') ?? (location.state as { from?: string } | null)?.from ?? null;

  // Utilizator deja autentificat: îl ducem exact acolo unde are sens pentru rolul lui.
  if (session.status === 'authenticated') {
    const fallback = session.user?.isPlatformAdmin ? '/admin' : '/app';
    return <Navigate to={from ?? fallback} replace />;
  }

  async function onSubmit(event: React.SyntheticEvent) {
    // `preventDefault` este esențial și este apelat în toate căile: într-un iframe `sandbox`
    // fără permisiunea `allow-forms`, browserul blochează trimiterea formularului nativ —
    // iar utilizatorul vede pagina „reîncărcându-se" fără nicio eroare. De aceea trimiterea este
    // declanșată din handler-ul de click, nu din comportamentul implicit al formularului.
    event.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      // `session.login` întoarce rezultatul doar după ce sesiunea a fost confirmată de server
      // (cu repliere automată pe token dacă mediul nu poate păstra cookie-uri). Dacă autentificarea
      // „reusește" dar sesiunea nu se confirmă, funcția aruncă și ajungem în `catch` cu un mesaj
      // explicit — niciodată cu o redirecționare în gol care se întoarce la login.
      const result = await session.login(email.trim(), password);
      const isPlatformAdmin = result?.user?.isPlatformAdmin === true;
      navigate(from ?? (isPlatformAdmin ? '/admin' : '/app'), { replace: true });
    } catch (err) {
      // 419 = cookie-ul CSRF nu a ajuns la server; „session-not-established" = sesiunea nu s-a
      // păstrat. În ambele cazuri cauza nu este parola, ci contextul de rulare: browsere care
      // blochează cookie-urile în cadre încorporate ori terță parte. Îi spunem clar ce are de făcut.
      if (err instanceof ApiError && (err.code === 'csrf-failed' || err.code === 'session-not-established')) {
        setNeedsOwnWindow(true);
      }
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-body-s text-ink-400 transition-ui hover:text-ink-600"
        >
          <Icon name="chevron-right" size={15} className="rotate-180" />
          Înapoi la site
        </Link>

        <div className="mb-8 flex flex-col gap-2">
          <h1 className="font-display text-display-m text-ink-950">Bine ai revenit</h1>
          <p className="text-body-s text-ink-600">
            Intră în cont pentru a-ți administra meniurile, codurile QR și ecranele.
          </p>
        </div>

        {error ? (
          <div className="mb-4 flex flex-col gap-3">
            <InlineAlert tone="danger">{error}</InlineAlert>
            {needsOwnWindow ? (
              <InlineAlert tone="warning" title="Browserul nu păstrează sesiunea în acest cadru">
                Aplicația rulează într-un cadru încorporat, unde browserul blochează cookie-urile de sesiune.
                Deschide-o într-o filă proprie și autentificarea va funcționa.
                <div className="mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(window.location.href, '_blank', 'noopener')}
                  >
                    Deschide într-o filă nouă
                  </Button>
                </div>
              </InlineAlert>
            ) : null}
          </div>
        ) : null}

        <form
          onSubmit={onSubmit}
          onKeyDown={(event) => {
            // Enter în orice câmp trimite formularul, chiar dacă sandbox-ul blochează
            // trimiterea nativă (implicit submission nu apucă să genereze click pe buton).
            const target = event.target as HTMLElement | null;
            if (event.key === 'Enter' && target && !target.matches('textarea')) {
              void onSubmit(event);
            }
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          <Field label="Email" required>
            {(props) => (
              <Input
                {...props}
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nume@restaurant.ro"
              />
            )}
          </Field>

          <Field label="Parolă" required>
            {(props) => (
              <Input
                {...props}
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>

          <div className="flex items-center justify-between">
            <Link
              to="/forgot-password"
              className="transition-ui text-body-s font-medium text-brand-600 hover:text-brand-700"
            >
              Am uitat parola
            </Link>
          </div>

          <Button type="submit" size="lg" loading={loading} onClick={onSubmit} className="w-full">
            Intră în cont
          </Button>
        </form>

        <p className="mt-6 text-body-s text-ink-600">
          Nu ai cont încă?{' '}
          <Link to="/register" className="transition-ui font-medium text-brand-600 hover:text-brand-700">
            Creează unul gratuit
          </Link>
        </p>

        <div className="mt-8 rounded-[var(--radius-md)] bg-surface-3/70 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Icon name="shield" size={16} className="text-ink-400" />
            <span className="text-caption font-medium text-ink-600">Conturi demo pentru prezentare</span>
          </div>
          <div className="flex flex-col gap-2">
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.email}
                type="button"
                onClick={() => {
                  setEmail(account.email);
                  setPassword(account.password);
                  setError(null);
                }}
                className="transition-ui flex items-center justify-between gap-3 rounded-[var(--radius-sm)] border border-ink-200 bg-surface px-3 py-2 text-left hover:border-brand-600"
              >
                <span className="flex flex-col">
                  <span className="text-body-s font-medium text-ink-800">{account.label}</span>
                  <span className="font-mono text-caption text-ink-400">{account.email}</span>
                </span>
                <span className="text-caption text-ink-400">{account.hint}</span>
              </button>
            ))}
          </div>
          <p className="mt-3 text-caption text-ink-400">
            Un click completează datele; apoi apasă „Intră în cont”. Conturile demo există doar în mediul de
            dezvoltare.
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
